/**
 * The typed MailSmith API client.
 *
 *   import { api, unwrap } from '@/api/client'
 *   const page = await unwrap(api.GET('/admin/v1/applications', { params: { query: { limit: 50 } } }))
 *
 * `api` is an openapi-fetch client typed from ./schema.d.ts. Middleware adds the
 * auth headers from the active AuthProvider and an X-Request-Id. `unwrap()`
 * returns the data or throws an ApiError (see ./errors.ts).
 */
import createClient, { type Middleware } from 'openapi-fetch'
import { getConfig } from '@/lib/config'
import { ApiError, networkError, toApiError } from './errors'
import type { paths } from './schema'

export type AuthHeaderSource = () => Promise<Record<string, string>>

let authHeaderSource: AuthHeaderSource | undefined
let authRefresher: AuthHeaderSource | undefined
let unauthorizedHandler: (() => void) | undefined

// Requests wait until an AuthProvider has installed its header source, so a
// request made during startup (a route loader, say) never goes out unsigned.
let markAuthReady: () => void = () => {}
let authReady = new Promise<void>((resolve) => (markAuthReady = resolve))

/** Called by the AuthProvider to supply headers (Bearer token or dev identity). */
export function setAuthHeaderSource(source: AuthHeaderSource): void {
  authHeaderSource = source
  markAuthReady()
}

/**
 * Called by the AuthProvider to supply headers from a forced token refresh.
 * After a 401, the client retries the request once with these headers before
 * reporting the session as expired.
 */
export function setAuthRefresher(refresher: AuthHeaderSource | undefined): void {
  authRefresher = refresher
}

/** For tests: forget the installed auth so the next request waits again. */
export function resetAuthForTests(): void {
  authHeaderSource = undefined
  authRefresher = undefined
  authReady = new Promise<void>((resolve) => (markAuthReady = resolve))
}

/** Called by the AuthProvider; invoked when any request returns 401. */
export function setUnauthorizedHandler(handler: (() => void) | undefined): void {
  unauthorizedHandler = handler
}

export function newRequestId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

// An unsent copy of each request, kept so a 401 can be retried once.
const retryCopies = new WeakMap<Request, Request>()

export const authMiddleware: Middleware = {
  async onRequest({ request }) {
    await authReady
    const source: AuthHeaderSource = authHeaderSource ?? (async () => ({}))
    const headers = await source()
    for (const [k, v] of Object.entries(headers)) request.headers.set(k, v)
    if (!request.headers.has('X-Request-Id')) request.headers.set('X-Request-Id', newRequestId())
    if (authRefresher) retryCopies.set(request, request.clone())
    return request
  },
  async onResponse({ request, response }) {
    if (response.status !== 401) return undefined
    const copy = retryCopies.get(request)
    retryCopies.delete(request)
    if (copy && authRefresher) {
      try {
        const headers = await authRefresher()
        for (const [k, v] of Object.entries(headers)) copy.headers.set(k, v)
        const retried = await fetch(copy)
        if (retried.status !== 401) return retried
      } catch {
        // Fall through: the refresher redirects to sign-in when it must.
      }
    }
    unauthorizedHandler?.()
    return undefined
  },
}

function baseUrl(): string {
  try {
    return getConfig().apiBaseUrl
  } catch {
    // main.tsx renders the configuration error; don't fail at import time.
    return ''
  }
}

export const api = createClient<paths>({ baseUrl: baseUrl() })
api.use(authMiddleware)

type FetchResult<T> = { data?: T; error?: unknown; response: Response }

/**
 * Await an openapi-fetch call and return its data, or throw ApiError.
 * Network failures (API down, CORS) become ApiError with status 0.
 */
export async function unwrap<T>(call: Promise<FetchResult<T>>): Promise<T> {
  let result: FetchResult<T>
  try {
    result = await call
  } catch (err) {
    if (err instanceof ApiError) throw err
    throw networkError(err)
  }
  const { data, error, response } = result
  if (!response.ok || error !== undefined) {
    throw toApiError(response.status, error, response.headers.get('X-Request-Id') ?? undefined)
  }
  // 204 No Content yields undefined data; callers typed for it get undefined.
  return data as T
}
